import { readonly, watch } from 'vue'

export const EMBED_PARAM = 'embed'
export const EMBED_VALUE = '1'
export const EMBED_SESSION_KEY = 'pdf_embed_mode'

export interface EmbedStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

interface EmbedModeSources {
  queryValue: unknown
  routeMetaEmbed: unknown
  sessionEnabled: boolean
}

/** 仅接受 embed=1；重复查询参数中包含 1 时同样生效。 */
export const isEmbedQueryEnabled = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => item === EMBED_VALUE)
  }

  return value === EMBED_VALUE
}

/** 聚合 URL、路由配置和当前标签页会话三类内嵌模式来源。 */
export const resolveEmbedMode = ({
  queryValue,
  routeMetaEmbed,
  sessionEnabled,
}: EmbedModeSources): boolean => {
  return isEmbedQueryEnabled(queryValue) || routeMetaEmbed === true || sessionEnabled
}

/** sessionStorage 不可用时安全降级，不阻断当前页面渲染。 */
export const readEmbedModeSession = (storage?: EmbedStorage | null): boolean => {
  if (!storage) return false

  try {
    return storage.getItem(EMBED_SESSION_KEY) === EMBED_VALUE
  } catch {
    return false
  }
}

/** 持久化失败仅影响跨页面保持，不影响 URL 或路由 Meta 的当前判定。 */
export const persistEmbedModeSession = (storage?: EmbedStorage | null): boolean => {
  if (!storage) return false

  try {
    storage.setItem(EMBED_SESSION_KEY, EMBED_VALUE)
    return true
  } catch {
    return false
  }
}

const getSessionStorage = (): EmbedStorage | null => {
  if (!import.meta.client) return null

  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/**
 * 统一管理内嵌模式：首次携带 embed=1 后，本标签页内的后续导航持续隐藏站点壳层。
 */
export const useEmbedMode = () => {
  const route = useRoute()
  const isEmbedMode = useState<boolean>('embed-mode', () => false)

  const detectEmbedMode = () => {
    const storage = getSessionStorage()
    const shouldEmbed = resolveEmbedMode({
      queryValue: route.query[EMBED_PARAM],
      routeMetaEmbed: (route.meta as Record<string, unknown>).embed,
      sessionEnabled: readEmbedModeSession(storage),
    })

    if (shouldEmbed) {
      persistEmbedModeSession(storage)
    }

    isEmbedMode.value = shouldEmbed
  }

  watch(() => route.fullPath, detectEmbedMode, { immediate: true })

  return {
    isEmbedMode: readonly(isEmbedMode),
    EMBED_PARAM,
  }
}
